/**
 * useStorylineTreeProps — the ONE place that assembles everything the shared
 * <AssetNavTree> needs for its "Storyline Menu" mode. Every page that hosts the tree
 * (Overview / Asset detail / UC detail / Storyline detail) calls this and spreads the
 * result, so the storyline wiring lives in a single spot instead of being hand-copied
 * (and half-forgotten) per page.
 *
 * Why this exists: the tree mode is a SHARED store, so a user who picks "Storyline
 * Menu" on one page keeps it on every other tree-hosting page. If a page forgot to
 * pass the storyline props (all optional on AssetNavTree), the tree silently collapsed
 * to all-Unassigned with no `+` — a whole class of bugs that only showed up by
 * clicking around. Centralizing the assembly makes those omissions structurally
 * impossible: add a new tree-hosting page, call this hook, done.
 *
 * Usage:
 *   const st = useStorylineTreeProps({ assets, industries, selectedStorylineId });
 *   <AssetNavTree assets={all} selectedAssetId={...} {...st.treeProps} ... />
 *   {st.modalNode}
 *
 * It owns: ListStorylines fetch (+ reload after create), the industry universe for the
 * tree + Create modal, storyline-node → detail navigation, and the Create modal (opened
 * by the tree's `+`). On create it navigates to the new storyline's detail page AND
 * bumps the reload key — the latter matters when creating FROM a storyline detail page,
 * where the target is the same /ivt/storylines/:id route so React Router does NOT
 * remount (the fetch wouldn't otherwise re-run).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AssetSummary, IndustryMeta, StorylineSummary } from '../../../client/IvtHubClient';
import { useStorylines } from './useStorylines';
import { useCreateStorylineModal } from './useCreateStorylineModal';

/** The storyline-specific slice of AssetNavTree props — spread onto the tree. */
export interface StorylineTreeProps {
  storylines: StorylineSummary[];
  menuIndustries: string[];
  onStorylineClick: (storylineId: string) => void;
  onCreateStoryline: (industry: string) => void;
  selectedStorylineId: string | null;
}

export interface StorylineTree {
  /** Spread onto <AssetNavTree {...treeProps} /> — the whole storyline prop bundle. */
  treeProps: StorylineTreeProps;
  /** The Create Storyline modal element to render (null when closed). */
  modalNode: ReactNode;
  /** Re-fetch the ListStorylines skeleton (tree node names/counts). Call after an
   *  edit elsewhere (e.g. the detail page's Edit modal changes a name) so the tree
   *  label updates without a page remount. */
  reloadStorylines: () => void;
}

export function useStorylineTreeProps({
  assets,
  industries,
  selectedStorylineId = null,
  reloadAssets,
}: {
  /** Full asset list (the Create modal's Choose-Assets options are scoped from it). */
  assets: AssetSummary[];
  /** ListIndustries universe — tree industry seeds + Create modal Industry picker. */
  industries: IndustryMeta[] | null;
  /** The storyline whose detail page is open (highlights + auto-expands its node). */
  selectedStorylineId?: string | null;
  /** The page's ListAssets reload (useAssetsData().reload). Called after a create so
   *  assets picked in the modal show under the new storyline WITHOUT a manual refresh:
   *  the tree hangs assets by asset.storylineLinks, which only refreshes when ListAssets
   *  is re-fetched (the storyline skeleton reload alone can't move assets). Omit only
   *  where the asset list can't gain links from a create (none of the current pages). */
  reloadAssets?: () => void;
}): StorylineTree {
  const navigate = useNavigate();
  // Bumped after a create so the tree re-fetches ListStorylines even when the page is
  // not remounted (creating from a storyline detail page → same route, no remount).
  const [reloadKey, setReloadKey] = useState(0);
  const storylines = useStorylines(reloadKey);

  const menuIndustries = useMemo(
    () => (industries ?? []).map((i) => i.industry).filter((n): n is string => !!n),
    [industries],
  );

  const createModal = useCreateStorylineModal({
    assets,
    industries: menuIndustries,
    onCreated: () => {
      setReloadKey((k) => k + 1); // refresh the storyline skeleton (new node)
      reloadAssets?.(); // refresh ListAssets so picked assets hang under the new node
    },
  });

  const treeProps: StorylineTreeProps = {
    storylines,
    menuIndustries,
    onStorylineClick: (storylineId) => navigate(`/ivt/storylines/${storylineId}`),
    onCreateStoryline: createModal.open,
    selectedStorylineId,
  };

  return {
    treeProps,
    modalNode: createModal.node,
    reloadStorylines: () => setReloadKey((k) => k + 1),
  };
}
